import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Send, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAnthropicChat } from '../../hooks/useAnthropicChat';
import './Participant.css';

const TOTAL_QUESTIONS = 7;

function TypingIndicator() {
  return (
    <div className="msg msg-ai">
      <div className="typing-dots">
        <span /><span /><span />
      </div>
    </div>
  );
}

export default function InterviewPage() {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const navigate = useNavigate();
  const { getAgent, updateSession, getSession } = useApp();

  const agent = getAgent(agentId);
  const session = getSession(sessionId);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [questionCount, setQuestionCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const { sendMessage, analyzeInterview, getInitialMessage, loading, error } = useAnthropicChat(
    agent || { name: '', objective: '', prompt: '', questionCount: 7, outputFields: [] }
  );

  useEffect(() => {
    if (agent && !started) {
      setStarted(true);
      setIsTyping(true);
      getInitialMessage().then(msg => {
        if (msg) {
          setMessages([{ role: 'assistant', content: msg }]);
          setQuestionCount(1);
        }
        setIsTyping(false);
      });
    }
  }, [agent]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || loading || isTyping) return;

    const userMsg = input.trim();
    setInput('');
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setIsTyping(true);

    if (questionCount >= TOTAL_QUESTIONS) {
      await handleEndInterview(newMessages);
      return;
    }

    const aiResponse = await sendMessage(userMsg, messages);
    if (aiResponse) {
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      setQuestionCount(c => c + 1);
    }
    setIsTyping(false);
  };

  const handleEndInterview = async (finalMessages) => {
    setIsTyping(true);
    const msgs = finalMessages || messages;
    const startTime = session?.created_at ? new Date(session.created_at) : new Date();
    const durationMinutes = Math.round((new Date() - startTime) / 60000);

    // حفظ الرسائل فوراً
    await updateSession(sessionId, {
      status: 'completed',
      messages: msgs,
      durationMinutes,
    });

    setIsTyping(false);
    navigate(`/interview/${agentId}/done?session=${sessionId}`);

    // التحليل يشتغل في الخلفية
    analyzeInterview(msgs).then(analysis => {
      if (analysis) updateSession(sessionId, { analysis });
    }).catch(e => console.error('Analysis failed:', e));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const progress = Math.min((questionCount / TOTAL_QUESTIONS) * 100, 100);

  if (!agent) return (
    <div className="participant-bg" style={{ justifyContent: 'center' }}>
      <div className="p-card"><p style={{ textAlign: 'center', color: 'var(--brown-muted)' }}>وكيل غير موجود</p></div>
    </div>
  );

  return (
    <div className="chat-bg">
      <div className="chat-container">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-agent-info">
            <div className="chat-avatar">ن</div>
            <div>
              <div className="chat-agent-name">نور – مساعدة أبحاث المستخدم</div>
              <div className="chat-agent-status">
                {isTyping ? '● يكتب...' : '● متصل'}
              </div>
            </div>
          </div>
          <button className="chat-end-btn" onClick={() => setShowEndConfirm(true)}>
            <X size={14} />
            إنهاء
          </button>
        </div>

        {/* Progress */}
        <div className="chat-progress">
          <div className="progress-info">
            <span>التقدم</span>
            <span>سؤال {Math.min(questionCount, TOTAL_QUESTIONS)} من {TOTAL_QUESTIONS}</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages">
          {error && (
            <div className="chat-error">
              ⚠️ {error}
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`msg ${msg.role === 'assistant' ? 'msg-ai' : 'msg-user'} animate-in`}>
              {msg.content}
            </div>
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="chat-input-bar">
          <textarea
            ref={inputRef}
            className="chat-textarea"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={questionCount >= TOTAL_QUESTIONS ? 'اضغط إرسال لإنهاء المقابلة...' : 'اكتب ردك هنا...'}
            rows={1}
            disabled={isTyping || loading}
          />
          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={!input.trim() || isTyping || loading}
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* End confirm modal */}
      {showEndConfirm && (
        <div className="modal-overlay" onClick={() => setShowEndConfirm(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--brown)', marginBottom: '0.75rem' }}>إنهاء المقابلة؟</h3>
            <p style={{ color: 'var(--brown-muted)', fontSize: '14px', marginBottom: '1.25rem' }}>
              هل أنت متأكد؟ سيتم حفظ إجاباتك حتى الآن.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => handleEndInterview()}>نعم، إنهاء</button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowEndConfirm(false)}>تراجع</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
